import moment from 'moment-timezone';

import { ROUTES } from '@/constants/routes';
import { formatFullName } from '@/helpers/name';
import { capitalize } from '@/helpers/string';
import { resolveJobDisplayName } from '@/helpers/workerJob';
import i18n from '@/i18n';
import { useAuthStore } from '@/modules/auth/store/authStore';

import type { FollowingEvent } from '@/components/following/helpers/sectionHelper';

import { mapWorkerBesoinHistoryEntryToFollowingEvent } from '@/components/following/helpers/besoinActivityToFollowingMapper';
import { buildFollowingLineCandidatures } from '@/components/following/helpers/jobApplicationFactory';
import { buildFollowingLineMission } from '@/components/following/helpers/missionFactory';
import {
	isInterviewReportProfileHistoryType,
	mapProfileHistoryItemToFollowingEvent,
	type ProfileHistoryFollowingMappingOptions,
} from '@/components/following/helpers/profileHistoryActivityToFollowingMapper';
import {
	WORKER_APPLIED_TYPE,
	type WorkerActivityItem,
	type WorkerActivityJobOfferItem,
	type WorkerActivityMissionItem,
	type WorkerActivityResponse,
	WorkerActivitySource,
} from '@/components/following/types';

const getUserNotSpecifiedLabel = (): string =>
	i18n.global.t('following.labels.userNotSpecified');

const formatDetail = (createdAt: string, name: string): string => {
	const date = moment(createdAt).format('HH:mm');
	return name ? `${date} - ${name}` : date;
};

const resolveActorName = (
	actorId: string | undefined,
	workerId?: string
): string => {
	if (!actorId) return '';
	const trimmedWorkerId = workerId?.trim();
	if (trimmedWorkerId && actorId.trim() === trimmedWorkerId) {
		return i18n.global.t('following.labels.profile');
	}
	const authStore = useAuthStore();
	const collaborator = authStore.getCollaborator(actorId);
	return collaborator ? formatFullName(collaborator) : '';
};

const resolveMissionCompanyName = (item: WorkerActivityMissionItem): string =>
	item.companyName ?? item.company?.name ?? '';

const resolveMissionDetailIso = (
	item: WorkerActivityMissionItem
): string | undefined => {
	if (item.timestamp) return moment(item.timestamp).toISOString();
	return undefined;
};

const mapMissionItemToEvent = (
	item: WorkerActivityMissionItem,
	workerId?: string
): FollowingEvent => {
	const detailIso = resolveMissionDetailIso(item);
	const actorName = resolveActorName(item.actorId, workerId);
	const detail = detailIso
		? formatDetail(detailIso, actorName)
		: getUserNotSpecifiedLabel();
	const line = buildFollowingLineMission({
		type: item.workerMissionStatus,
		date: moment(item.timestamp).toISOString(),
		jobTitle: item.jobId ? resolveJobDisplayName({ jobId: item.jobId }) : '',
		companyName: resolveMissionCompanyName(item),
		missionTo: {
			name: ROUTES.APP.COMPANY_NEED.MISSIONS,
			query: { search: item.name ?? '' },
		},
		detail,
	});
	return {
		id: item.id,
		createdAt: new Date(item.timestamp),
		line,
		agencyId: item.agencyId,
		source: WorkerActivitySource.MISSIONS,
	};
};

const mapJobOfferItemToEvent = (
	item: WorkerActivityJobOfferItem
): FollowingEvent | null => {
	if (item.type?.trim().toLowerCase() !== WORKER_APPLIED_TYPE) return null;

	const dateIso = item.date || undefined;
	const applicationSource = item.applicationSource?.trim();
	const detail = dateIso
		? formatDetail(
				dateIso,
				applicationSource && applicationSource.toLowerCase() !== 'unknown'
					? (capitalize(applicationSource) ?? '')
					: ''
			)
		: getUserNotSpecifiedLabel();
	const besoinId = item.besoinId?.trim();
	const line = buildFollowingLineCandidatures({
		date: new Date(item.date).getTime(),
		announceName: item.jobOfferName?.trim() || '',
		besoinName: item.besoinName?.trim() || '',
		...(besoinId
			? {
					besoinTo: {
						name: ROUTES.APP.COMPANY_NEED.DETAIL.MATCHING,
						params: { companyNeedId: besoinId },
					},
				}
			: {}),
		detail,
	});
	return {
		id: item.id,
		createdAt: new Date(item.date),
		line,
		source: WorkerActivitySource.JOBOFFERS,
	};
};

const withEnvelopeIdentity = (
	item: WorkerActivityItem,
	event: FollowingEvent | null
): FollowingEvent | null => {
	if (!event) return null;
	return {
		...event,
		id: item.id,
		createdAt: new Date(item.occurredAt),
		source: item.source,
		...(item.source === WorkerActivitySource.PROFILE_HISTORY
			? { profileHistoryType: item.payload.type }
			: {}),
	};
};

const withInterviewContext = (
	item: WorkerActivityItem,
	event: FollowingEvent | null
): FollowingEvent | null => {
	if (
		!event ||
		item.source !== WorkerActivitySource.PROFILE_HISTORY ||
		!isInterviewReportProfileHistoryType(item.payload.type)
	) {
		return event;
	}
	return {
		...event,
		line: { ...event.line, interviewContext: { interviewId: item.id } },
	};
};

const mapActivityItemToEvent = (
	item: WorkerActivityItem,
	options?: ActivityFollowingMappingOptions
): FollowingEvent | null => {
	switch (item.source) {
		case WorkerActivitySource.BESOIN:
			return withEnvelopeIdentity(
				item,
				mapWorkerBesoinHistoryEntryToFollowingEvent(item.payload)
			);
		case WorkerActivitySource.MISSIONS:
			return withEnvelopeIdentity(
				item,
				mapMissionItemToEvent(item.payload, options?.workerId)
			);
		case WorkerActivitySource.JOBOFFERS:
			return withEnvelopeIdentity(item, mapJobOfferItemToEvent(item.payload));
		case WorkerActivitySource.PROFILE_HISTORY:
			return withInterviewContext(
				item,
				withEnvelopeIdentity(
					item,
					mapProfileHistoryItemToFollowingEvent(item.payload, options)
				)
			);
		default: {
			const _exhaustive: never = item;
			return _exhaustive;
		}
	}
};

export type ActivityFollowingMappingOptions =
	ProfileHistoryFollowingMappingOptions;

export const activityResponseToFollowingEvents = (
	response: WorkerActivityResponse,
	options?: ActivityFollowingMappingOptions
): FollowingEvent[] => {
	const events: FollowingEvent[] = [];
	for (const item of response.items) {
		const event = mapActivityItemToEvent(item, options);
		if (event) events.push(event);
	}
	return events;
};
